/**
 * WebAuthn helpers aligned with django-allauth / fido2 JSON (base64url) payloads.
 */

import i18n from '@/i18n';

function bufferToBase64url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

function base64urlToBuffer(base64url: string): ArrayBuffer {
  const padding = '='.repeat((4 - (base64url.length % 4)) % 4);
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/') + padding;
  const binary = atob(base64);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out.buffer;
}

type OptionsJSON = Record<string, unknown>;

type PublicKeyCredentialStatic = {
  parseRequestOptionsFromJSON?: (o: OptionsJSON) => PublicKeyCredentialRequestOptions;
  parseCreationOptionsFromJSON?: (o: OptionsJSON) => PublicKeyCredentialCreationOptions;
};

function publicKeyCredentialApi(): PublicKeyCredentialStatic | null {
  if (typeof PublicKeyCredential === 'undefined') {
    return null;
  }
  return PublicKeyCredential as unknown as PublicKeyCredentialStatic;
}

/**
 * fido2 wraps options in `{ publicKey: { ... } }` (CredentialRequestOptions).
 * The browser APIs and our manual parser need the inner PublicKeyCredentialRequestOptions.
 */
function unwrapPublicKey(options: OptionsJSON): OptionsJSON {
  if ('publicKey' in options && typeof options.publicKey === 'object' && options.publicKey !== null) {
    return options.publicKey as OptionsJSON;
  }
  return options;
}

function parseRequestOptions(options: OptionsJSON): PublicKeyCredentialRequestOptions {
  const inner = unwrapPublicKey(options);
  const PK = publicKeyCredentialApi();
  if (PK && typeof PK.parseRequestOptionsFromJSON === 'function') {
    return PK.parseRequestOptionsFromJSON(inner);
  }
  const challenge = inner.challenge;
  if (typeof challenge !== 'string') throw new Error('Invalid WebAuthn request options');
  const allowCredentials = Array.isArray(inner.allowCredentials)
    ? (inner.allowCredentials as { type: string; id: string }[]).map((c) => ({
        type: c.type as PublicKeyCredentialType,
        id: base64urlToBuffer(c.id),
      }))
    : undefined;
  const base: PublicKeyCredentialRequestOptions = {
    challenge: base64urlToBuffer(challenge),
    allowCredentials,
    rpId: typeof inner.rpId === 'string' ? inner.rpId : undefined,
    timeout: typeof inner.timeout === 'number' ? inner.timeout : undefined,
    userVerification: inner.userVerification as UserVerificationRequirement | undefined,
    extensions: inner.extensions as AuthenticationExtensionsClientInputs | undefined,
  };
  if ('hints' in inner && Array.isArray(inner.hints)) {
    (base as PublicKeyCredentialRequestOptions & { hints?: unknown }).hints = inner.hints;
  }
  return base;
}

function parseCreationOptions(options: OptionsJSON): PublicKeyCredentialCreationOptions {
  const inner = unwrapPublicKey(options);
  const PK = publicKeyCredentialApi();
  if (PK && typeof PK.parseCreationOptionsFromJSON === 'function') {
    return PK.parseCreationOptionsFromJSON(inner);
  }
  const challenge = inner.challenge;
  const rp = inner.rp as Record<string, unknown> | undefined;
  const user = inner.user as Record<string, unknown> | undefined;
  if (typeof challenge !== 'string' || !rp || !user) {
    throw new Error('Invalid WebAuthn creation options');
  }
  const uid = user.id;
  if (typeof uid !== 'string') throw new Error('Invalid WebAuthn user id');
  const pubKeyCredParams = (inner.pubKeyCredParams as PublicKeyCredentialParameters[]) ?? [];
  const excludeCredentials = Array.isArray(inner.excludeCredentials)
    ? (inner.excludeCredentials as { type: string; id: string }[]).map((c) => ({
        type: c.type as PublicKeyCredentialType,
        id: base64urlToBuffer(c.id),
      }))
    : undefined;
  return {
    rp: { id: String(rp.id), name: String(rp.name) },
    user: {
      id: base64urlToBuffer(uid),
      name: String(user.name),
      displayName: String(user.displayName ?? user.name),
    },
    challenge: base64urlToBuffer(challenge),
    pubKeyCredParams,
    timeout: typeof inner.timeout === 'number' ? inner.timeout : undefined,
    authenticatorSelection: inner.authenticatorSelection as AuthenticatorSelectionCriteria | undefined,
    attestation: inner.attestation as AttestationConveyancePreference | undefined,
    excludeCredentials,
    extensions: inner.extensions as AuthenticationExtensionsClientInputs | undefined,
  };
}

function credentialToJson(credential: PublicKeyCredential): Record<string, unknown> {
  const withJson = credential as PublicKeyCredential & {
    toJSON?: () => Record<string, unknown>;
  };
  if (typeof withJson.toJSON === 'function') {
    return withJson.toJSON();
  }
  const r = credential.response as AuthenticatorAssertionResponse;
  return {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: credential.type,
    clientExtensionResults: credential.getClientExtensionResults(),
    response: {
      clientDataJSON: bufferToBase64url(r.clientDataJSON),
      authenticatorData: bufferToBase64url(r.authenticatorData),
      signature: bufferToBase64url(r.signature),
      userHandle:
        r.userHandle && r.userHandle.byteLength > 0 ? bufferToBase64url(r.userHandle) : null,
    },
  };
}

function registrationCredentialToJson(credential: PublicKeyCredential): Record<string, unknown> {
  const withJson = credential as PublicKeyCredential & {
    toJSON?: () => Record<string, unknown>;
  };
  if (typeof withJson.toJSON === 'function') {
    return withJson.toJSON();
  }
  const r = credential.response as AuthenticatorAttestationResponse;
  const base: Record<string, unknown> = {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: credential.type,
    clientExtensionResults: credential.getClientExtensionResults(),
    response: {
      clientDataJSON: bufferToBase64url(r.clientDataJSON),
      attestationObject: bufferToBase64url(r.attestationObject),
    },
  };
  if ('getTransports' in r && typeof r.getTransports === 'function') {
    base.transports = r.getTransports();
  }
  return base;
}

export function webAuthnSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.isSecureContext === true &&
    typeof PublicKeyCredential !== 'undefined'
  );
}

export async function webAuthnGetAssertion(
  requestOptions: OptionsJSON
): Promise<Record<string, unknown>> {
  const publicKey = parseRequestOptions(requestOptions);
  const credential = await navigator.credentials.get({ publicKey });
  if (!credential || credential.type !== 'public-key') {
    throw new Error('No passkey credential returned');
  }
  return credentialToJson(credential as PublicKeyCredential);
}

export async function webAuthnCreateCredential(
  creationOptions: OptionsJSON
): Promise<Record<string, unknown>> {
  const publicKey = parseCreationOptions(creationOptions);
  const credential = await navigator.credentials.create({ publicKey });
  if (!credential || credential.type !== 'public-key') {
    throw new Error('No passkey credential created');
  }
  return registrationCredentialToJson(credential as PublicKeyCredential);
}

export function webAuthnErrorMessage(err: unknown): string {
  const t = (key: 'account.webauthn.notAllowed' | 'account.webauthn.requestFailed' | 'account.webauthn.genericError') =>
    String(i18n.global.t(key));
  if (err instanceof DOMException) {
    if (err.name === 'NotAllowedError') {
      return t('account.webauthn.notAllowed');
    }
    if (err.name === 'AbortError' || err.name === 'SecurityError') {
      return err.message || t('account.webauthn.requestFailed');
    }
    return err.message || t('account.webauthn.requestFailed');
  }
  if (err instanceof Error) return err.message;
  return t('account.webauthn.genericError');
}
